import { Route, Routes } from 'react-router-dom';
import Layout from 'pages/layout';
import { Suspense, lazy } from 'react';
import {
  AuthenticateRoutes,
  UnAuthenticateRoutes
} from 'pages/layout/PrivateRoute';
import GroupList from 'pages/modules/friendList/groupList';
import { useAppSelector } from 'store/hook';
import { RootState } from 'store';
import { OpenApp } from 'pages/modules/open-app/openApp';
import { appAccountValidation, swmAccountValidation } from 'utilities/authTokenValidation';
import BlogReadingPage from 'pages/modules/aboutus/blogReadingPage';

const Loadable = (Component: any) => (props: JSX.IntrinsicAttributes) => (
  <Suspense>
    <Component {...props} />
  </Suspense>
);

const Landing = Loadable(lazy(() => import('pages/authentication/splash')));
const Home = Loadable(lazy(() => import('pages/modules/home')));
const Privacy = Loadable(lazy(() => import('pages/modules/privacy')));
const ViewPost = Loadable(lazy(() => import('pages/modules/home/viewPost')));
const MediaPost = Loadable(lazy(() => import('pages/modules/home/mediaPost')));
const Groups = Loadable(lazy(() => import('pages/modules/groups')));
const Chat = Loadable(lazy(() => import('pages/modules/chats')));
const CreatePost = Loadable(lazy(() => import('pages/modules/create/createPost')));
const CreatePoll = Loadable(lazy(() => import('pages/modules/create/createPoll')));
const Notification = Loadable(lazy(() => import('pages/modules/notification')));
const Search = Loadable(lazy(() => import('pages/modules/search')));

const Userprofile = Loadable(
  lazy(() => import('pages/modules/profile/userProfile'))
);
const FriendList = Loadable(
  lazy(() => import('pages/modules/friendList/friendList'))
);
const ChatWindow = Loadable(
  lazy(() => import('pages/modules/chats/chatWindow'))
);
const GroupChatWindow = Loadable(
  lazy(() => import('pages/modules/chats/groupChatWindow'))
);
const GroupPost = Loadable(
  lazy(() => import('pages/modules/groups/postGroups/groupPost'))
);
const CrateGroup = Loadable(
  lazy(() => import('pages/modules/groups/createGroup'))
);
const About4um = Loadable(lazy(() => import('pages/authentication/About4um')));
const Consent = Loadable(lazy(() => import('pages/authentication/Consent')));
const NotFound = Loadable(lazy(() => import('pages/modules/notFound')));

const Routers = () => {
  const { isChatEnabled }: any = useAppSelector(
    (state: RootState) => state.featureFlag
  );
  const { authToken }: any = useAppSelector(
    (state: RootState) => state.userAuth
  );
  const isAppAccount = appAccountValidation?.(authToken ?? null);
  const isSwmUser = swmAccountValidation?.(authToken ?? null);

  return (
    <>
      <Routes>
        <Route path='/aboutus' element={<About4um />} />
        <Route path="/about-us/blog/:id" element={<BlogReadingPage />} />
        <Route element={<UnAuthenticateRoutes />}>
          <Route path='/signin' element={<Landing />} />
          <Route path='/privacy' element={<Privacy />} />
          <Route element={<Layout />}>
            <Route path='/' element={<Home />} />
          </Route>
        </Route>
        {/* Outside auth wrappers so login-state changes cannot remount /consent */}
        <Route path='/consent' element={<Consent />} />
        <Route element={<Layout />}>
          <Route path='/group/:groupId' element={<GroupPost />} />
          <Route path='/profile/:userId' element={<Userprofile />} />
          <Route path='/post/:postId' element={<ViewPost />} />
          <Route element={<AuthenticateRoutes />}>
            <Route path='/home' element={<Home />} />            
            <Route path='/mediaPost/:postId' element={<MediaPost />} />
            {!(isAppAccount || isSwmUser) &&
              <Route path='/groups' element={<Groups />} />
            }
            <Route path='/createPost' element={<CreatePost />} />
            <Route path='/createPoll' element={<CreatePoll />} />
            {isChatEnabled && !(isAppAccount || isSwmUser) &&
              <>
                <Route path='/chat' element={<Chat />} />
                <Route path='/chat/:conversationId' element={<ChatWindow />} />
                <Route path='/chat/groups/:groupConversationId' element={<GroupChatWindow />} />
              </>
            }            
            <Route path='/friendlist/:userId' element={<FriendList />} />
            <Route path='/grouplist/:userId' element={<GroupList />} />
            <Route path='/createGroup' element={isAppAccount || isSwmUser? <NotFound />:<CrateGroup />} />
            <Route path='/notification' element={<Notification />} />
            <Route path='/search' element={<Search />} />
            <Route path='*' element={<NotFound />} />
            <Route path="/openApp" element={<OpenApp />} />
          </Route>
        </Route>
      </Routes>
    </>
  )
};

export default Routers;
